import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import {
    BugIcon,
    ChatTeardropDotsIcon,
    LightbulbIcon,
    LifebuoyIcon,
    EnvelopeSimpleIcon,
    TrashIcon,
    SignOutIcon,
    CheckCircleIcon,
    ClockIcon,
    XCircleIcon,
    HourglassIcon,
    ArrowsClockwiseIcon,
    MegaphoneIcon,
} from "@phosphor-icons/react";
import BroadcastsPanel from "@/pages/admin/BroadcastsPanel";

const CATEGORY_META = {
    bug: { label: "Bug", icon: BugIcon, color: "#E01A4F" },
    feedback: { label: "Feedback", icon: ChatTeardropDotsIcon, color: "#FFA700" },
    feature: { label: "Feature", icon: LightbulbIcon, color: "#E01A4F" },
    support: { label: "Support", icon: LifebuoyIcon, color: "#FFA700" },
};

const STATUS_META = {
    open: { label: "Open", color: "#FFA700", icon: ClockIcon },
    in_progress: { label: "In progress", color: "#3B82F6", icon: HourglassIcon },
    resolved: { label: "Resolved", color: "#10B981", icon: CheckCircleIcon },
    closed: { label: "Closed", color: "#6B7280", icon: XCircleIcon },
};

const STATUS_ORDER = ["open", "in_progress", "resolved", "closed"];

export default function AdminDashboard() {
    const { admin, ready, logout } = useAdminAuth();
    const navigate = useNavigate();

    const [tab, setTab] = useState("tickets");
    const [stats, setStats] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [messages, setMessages] = useState([]);
    const [delRequests, setDelRequests] = useState([]);
    const [filter, setFilter] = useState({ category: "all", status: "all" });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (ready && (!admin || typeof admin !== "object")) {
            navigate("/admin/login");
        }
    }, [admin, ready, navigate]);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const [s, t, m, d] = await Promise.all([
                api.get("/admin/stats"),
                api.get(`/admin/tickets?category=${filter.category}&status=${filter.status}&limit=100`),
                api.get("/admin/contact-messages?limit=100"),
                api.get("/admin/delete-requests?limit=100"),
            ]);
            setStats(s.data);
            setTickets(t.data);
            setMessages(m.data);
            setDelRequests(d.data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        if (admin && typeof admin === "object") fetchAll();
    }, [admin, fetchAll]);

    const updateStatus = async (ticketId, status) => {
        try {
            await api.patch(`/admin/tickets/${ticketId}`, { status });
            setTickets((arr) =>
                arr.map((t) => (t.id === ticketId ? { ...t, status } : t))
            );
            // refresh stats
            const s = await api.get("/admin/stats");
            setStats(s.data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    if (!ready || !admin || typeof admin !== "object") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-bg text-text-secondary">
                Loading...
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg text-white">
            <Seo title="Admin Dashboard — India Reels" description="India Reels admin panel" path="/admin" />

            <header className="border-b border-white/5 glass sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-5 md:px-10 py-3.5 flex items-center justify-between">
                    <Link to="/" data-testid="admin-logo">
                        <Logo size={36} textSize="text-2xl" />
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-semibold">{admin.name}</div>
                            <div className="text-[11px] text-text-secondary">{admin.email}</div>
                        </div>
                        <button
                            onClick={fetchAll}
                            className="btn-ghost px-3 py-2 flex items-center gap-2"
                            data-testid="admin-refresh-btn"
                            title="Refresh"
                        >
                            <ArrowsClockwiseIcon size={18} weight="bold" />
                        </button>
                        <button
                            onClick={async () => {
                                await logout();
                                navigate("/admin/login");
                            }}
                            data-testid="admin-logout-btn"
                            className="btn-primary px-4 py-2 flex items-center gap-2 text-sm"
                        >
                            <SignOutIcon size={16} weight="bold" />
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-5 md:px-10 py-8">
                <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-2">
                    admin dashboard
                </div>
                <h1 className="font-heading font-black text-3xl md:text-5xl leading-tight">
                    Hello, <span className="gradient-text">{admin.name.split(" ")[0]}</span>.
                </h1>

                {/* Stats */}
                {stats && (
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
                        <StatCard label="Total tickets" value={stats.total_tickets} hint="all submissions" color="#E01A4F" />
                        <StatCard label="Open tickets" value={stats.open_tickets} hint="needs attention" color="#FFA700" />
                        <StatCard label="Contact messages" value={stats.total_contact_messages} hint="from contact form" color="#E01A4F" />
                        <StatCard label="Delete requests" value={stats.total_delete_requests} hint="account closures" color="#FFA700" />
                    </div>
                )}

                {/* Tabs */}
                <div className="mt-10 flex flex-wrap gap-2 border-b border-white/10">
                    <TabBtn active={tab === "tickets"} onClick={() => setTab("tickets")} testid="tab-tickets">
                        Support Tickets
                        <Pill>{tickets.length}</Pill>
                    </TabBtn>
                    <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testid="tab-messages">
                        Contact Messages
                        <Pill>{messages.length}</Pill>
                    </TabBtn>
                    <TabBtn active={tab === "delete"} onClick={() => setTab("delete")} testid="tab-delete">
                        Delete Requests
                        <Pill>{delRequests.length}</Pill>
                    </TabBtn>
                    <TabBtn active={tab === "broadcasts"} onClick={() => setTab("broadcasts")} testid="tab-broadcasts">
                        <MegaphoneIcon size={14} weight="fill" />
                        Broadcasts
                    </TabBtn>
                </div>

                {err && (
                    <div className="mt-4 text-primary text-sm bg-primary/10 px-4 py-3 rounded-xl" data-testid="admin-error">
                        {err}
                    </div>
                )}

                {loading && (
                    <div className="mt-6 text-text-secondary">Loading...</div>
                )}

                {/* Tab content */}
                {tab === "tickets" && (
                    <div className="mt-6">
                        <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="ticket-filters">
                            <Select
                                label="Category"
                                value={filter.category}
                                onChange={(v) => setFilter((f) => ({ ...f, category: v }))}
                                options={[
                                    { value: "all", label: "All categories" },
                                    { value: "bug", label: "Bug" },
                                    { value: "feedback", label: "Feedback" },
                                    { value: "feature", label: "Feature" },
                                    { value: "support", label: "Support" },
                                ]}
                                testid="filter-category"
                            />
                            <Select
                                label="Status"
                                value={filter.status}
                                onChange={(v) => setFilter((f) => ({ ...f, status: v }))}
                                options={[
                                    { value: "all", label: "All statuses" },
                                    { value: "open", label: "Open" },
                                    { value: "in_progress", label: "In progress" },
                                    { value: "resolved", label: "Resolved" },
                                    { value: "closed", label: "Closed" },
                                ]}
                                testid="filter-status"
                            />
                        </div>

                        {tickets.length === 0 && !loading && (
                            <EmptyState text="No tickets matching the current filter." />
                        )}

                        <div className="space-y-3">
                            {tickets.map((t) => {
                                const cm = CATEGORY_META[t.category] || CATEGORY_META.support;
                                const sm = STATUS_META[t.status] || STATUS_META.open;
                                return (
                                    <div
                                        key={t.id}
                                        data-testid={`ticket-${t.id}`}
                                        className="rounded-3xl border border-white/10 bg-surface p-5 md:p-6"
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div
                                                    className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
                                                    style={{ background: `${cm.color}1F` }}
                                                >
                                                    <cm.icon size={20} weight="fill" color={cm.color} />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: cm.color }}>
                                                            {cm.label}
                                                        </span>
                                                        <StatusBadge status={t.status} />
                                                    </div>
                                                    <div className="font-heading font-bold text-base md:text-lg mt-0.5 truncate">
                                                        {t.subject}
                                                    </div>
                                                    <div className="text-xs text-text-secondary">
                                                        {t.name} · {t.email}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <select
                                                    value={t.status}
                                                    onChange={(e) => updateStatus(t.id, e.target.value)}
                                                    className="input-field py-2 px-3 text-xs"
                                                    style={{ minWidth: 130 }}
                                                    data-testid={`status-select-${t.id}`}
                                                >
                                                    {STATUS_ORDER.map((s) => (
                                                        <option key={s} value={s}>{STATUS_META[s].label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <p className="mt-4 text-white/80 leading-relaxed whitespace-pre-wrap text-sm">
                                            {t.message}
                                        </p>
                                        {(t.device || t.app_version) && (
                                            <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-text-secondary">
                                                {t.device && <span className="px-2 py-1 rounded-full bg-white/5">📱 {t.device}</span>}
                                                {t.app_version && <span className="px-2 py-1 rounded-full bg-white/5">v{t.app_version}</span>}
                                            </div>
                                        )}
                                        <div className="mt-3 text-[11px] text-text-secondary">
                                            Submitted {new Date(t.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {tab === "messages" && (
                    <div className="mt-6 space-y-3">
                        {messages.length === 0 && !loading && (
                            <EmptyState text="No contact form messages yet." />
                        )}
                        {messages.map((m) => (
                            <div
                                key={m.id}
                                data-testid={`message-${m.id}`}
                                className="rounded-3xl border border-white/10 bg-surface p-5 md:p-6"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
                                        <EnvelopeSimpleIcon size={20} weight="duotone" color="#E01A4F" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold text-base">{m.subject}</div>
                                        <div className="text-xs text-text-secondary">{m.name} · {m.email}</div>
                                    </div>
                                </div>
                                <p className="mt-3 text-white/80 leading-relaxed whitespace-pre-wrap text-sm">{m.message}</p>
                                <div className="mt-3 text-[11px] text-text-secondary">
                                    {new Date(m.created_at).toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "delete" && (
                    <div className="mt-6 space-y-3">
                        {delRequests.length === 0 && !loading && (
                            <EmptyState text="No account deletion requests yet." />
                        )}
                        {delRequests.map((d) => (
                            <div
                                key={d.id}
                                data-testid={`delreq-${d.id}`}
                                className="rounded-3xl border border-white/10 bg-surface p-5 md:p-6"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-2xl bg-secondary/15 flex items-center justify-center shrink-0">
                                        <TrashIcon size={20} weight="duotone" color="#FFA700" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold text-base">{d.email}</div>
                                        <div className="text-xs text-text-secondary">
                                            {d.username || "no username"} · status: {d.status}
                                        </div>
                                    </div>
                                </div>
                                {d.reason && (
                                    <p className="mt-3 text-white/80 leading-relaxed text-sm">
                                        <span className="text-text-secondary">Reason:</span> {d.reason}
                                    </p>
                                )}
                                <div className="mt-3 text-[11px] text-text-secondary">
                                    {new Date(d.created_at).toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "broadcasts" && (
                    <div className="mt-6">
                        <BroadcastsPanel />
                    </div>
                )}
            </main>
        </div>
    );
}

function StatCard({ label, value, hint, color }) {
    return (
        <div className="rounded-3xl border border-white/10 bg-surface p-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-text-secondary font-bold mb-2">{label}</div>
            <div className="font-heading font-black text-3xl md:text-4xl" style={{ color }}>{value ?? 0}</div>
            <div className="text-xs text-text-secondary mt-1">{hint}</div>
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-3 -mb-px text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                active
                    ? "border-primary text-white"
                    : "border-transparent text-text-secondary hover:text-white"
            }`}
        >
            {children}
        </button>
    );
}

function Pill({ children }) {
    return (
        <span className="text-[10px] font-bold bg-white/10 rounded-full px-2 py-0.5">
            {children}
        </span>
    );
}

function StatusBadge({ status }) {
    const sm = STATUS_META[status] || STATUS_META.open;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold"
            style={{ background: `${sm.color}22`, color: sm.color }}
        >
            <sm.icon size={11} weight="fill" />
            {sm.label}
        </span>
    );
}

function Select({ label, value, onChange, options, testid }) {
    return (
        <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-text-secondary font-bold">
                {label}
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="input-field py-2 px-3 text-sm"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </div>
    );
}

function EmptyState({ text }) {
    return (
        <div className="text-center py-16 text-text-secondary border border-dashed border-white/10 rounded-3xl">
            {text}
        </div>
    );
}
